import { useState, useEffect } from "react";
import {
  Button,
  Form,
  Modal,
  Stack,
  Col,
  Row,
  Alert,
  ListGroup,
} from "react-bootstrap";
import { HttpData, HttpNoData } from "../../../../Core";
import { HiPlus } from "react-icons/hi";

export default function ProductModalCreate({
  jwt,
  show,
  categories,
  preselectedCategoryId,
  subtypes,
  materials,
  onHide,
  onCreated,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState(
    preselectedCategoryId || (categories.length > 0 ? categories[0].id : -1),
  );
  const [maxSize, setMaxSize] = useState("");
  const [minSize, setMinSize] = useState("");
  const [standardThickness, setStandardThickness] = useState("");
  const [standardWeight, setStandardWeight] = useState("");
  const [standardSize, setStandardSize] = useState("");
  const [standardPrice, setStandardPrice] = useState("");
  const [isSimpleProduct, setIsSimpleProduct] = useState(false);
  const [subtypeId, setSubtypeId] = useState("");
  const [materialId, setMaterialId] = useState("");

  const [useExactWeights, setUseExactWeights] = useState(false);

  const [allCoatings, setAllCoatings] = useState([]);
  const [selectedCoatings, setSelectedCoatings] = useState([]);
  const [showCoatingsModal, setShowCoatingsModal] = useState(false);

  useEffect(() => {
    if (preselectedCategoryId) {
      setCategoryId(preselectedCategoryId);
    }
    async function loadCoatings() {
      const response = await HttpNoData(
        "/api/v1/calculateOrderCost/coatings",
        "GET",
        jwt,
      );
      setAllCoatings(response.data ?? []);
    }
    loadCoatings();
  }, [preselectedCategoryId, jwt]);

  const addCoating = (coatingId) => {
    const coating = allCoatings.find((c) => c.id === coatingId);
    if (coating && !selectedCoatings.find((sc) => sc.id === coatingId)) {
      setSelectedCoatings([...selectedCoatings, coating]);
    }
  };

  const removeCoating = (coatingId) => {
    setSelectedCoatings(selectedCoatings.filter((c) => c.id !== coatingId));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const response = await HttpData(
      "/api/v1/calculateOrderCost/products",
      "POST",
      jwt,
      {
        name: name,
        description: description,
        categoryId: +categoryId,
        maxSize: maxSize === "" ? -1 : +maxSize,
        minSize: minSize === "" ? -1 : +minSize,
        standardThickness: standardThickness === "" ? -1 : +standardThickness,
        standardWeight: +standardWeight,
        standardSize: standardSize === "" ? -1 : +standardSize,
        standardPrice: standardSize === "" ? -1 : +standardPrice,
        materialId: materialId ? +materialId : null,
        subtypeId: subtypeId ? +subtypeId : null,
        useExactWeights: useExactWeights,
      },
    );

    if (response.statusSuccessful) {
      const createdProduct = response.data;

      if (isSimpleProduct) {
        const sizeResponse = await HttpData(
          `/api/v1/calculateOrderCost/productRangeSizes`,
          "POST",
          jwt,
          {
            price: +standardPrice,
            size: -1,
            productId: createdProduct.id,
          },
        );
        if (!sizeResponse.statusSuccessful)
          alert("Не получилось создать стоимость");
      }

      for (const coating of selectedCoatings) {
        const coatingResponse = await HttpNoData(
          `/api/v1/calculateOrderCost/products/${createdProduct.id}/coatings/${coating.id}`,
          "POST",
          jwt,
        );
        if (!coatingResponse.statusSuccessful) {
          console.warn(`Не удалось добавить нанесение: ${coating.name}`);
        }
      }

      onCreated(createdProduct);
      setName("");
      setDescription("");
      setSubtypeId("");
      setMaterialId("");
      setMaxSize("");
      setMinSize("");
      setStandardThickness("");
      setStandardWeight("");
      setStandardSize("");
      setStandardPrice("");
      setSelectedCoatings([]);
      setUseExactWeights(false);
    } else alert(`Не получилось создать: ${response.error}`);
  };

  const CoatingsSelectionModal = () => (
    <Modal
      show={showCoatingsModal}
      onHide={() => setShowCoatingsModal(false)}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Выбор нанесений для продукта</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            <h6>Доступные нанесения</h6>
            <ListGroup>
              {allCoatings
                .filter(
                  (coating) =>
                    !selectedCoatings.find((sc) => sc.id === coating.id),
                )
                .map((coating) => (
                  <ListGroup.Item key={coating.id}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{coating.name}</strong>
                      </div>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => addCoating(coating.id)}
                      >
                        <HiPlus />
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
            </ListGroup>
          </Col>
          <Col md={6}>
            <h6>Выбранные нанесения</h6>
            <ListGroup>
              {selectedCoatings.map((coating) => (
                <ListGroup.Item key={coating.id}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{coating.name}</strong>
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeCoating(coating.id)}
                    >
                      ×
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
              {selectedCoatings.length === 0 && (
                <Alert variant="info" className="small mb-0">
                  Нанесения не выбраны
                </Alert>
              )}
            </ListGroup>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowCoatingsModal(false)}>
          Готово
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <>
      <Modal show={show} onHide={onHide}>
        <Form onSubmit={onSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Создание продукции</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Check
              type="switch"
              className="me-auto"
              checked={isSimpleProduct}
              onChange={(e) => {
                setIsSimpleProduct(e.target.checked);
                setMaxSize("");
                setMinSize("");
                setStandardSize("");
                setStandardThickness("");
                setStandardWeight("");
                setStandardPrice("");
              }}
              label={"Простая продукция"}
            />
            <Form.Group className="mb-3">
              <Form.Label>Название</Form.Label>
              <Form.Control
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Описание</Form.Label>
              <Form.Control
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Категория</Form.Label>
              <Form.Select
                required
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                {categories.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Нанесения</Form.Label>
              <div className="border rounded p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Выбрано нанесений: {selectedCoatings.length}</span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowCoatingsModal(true)}
                  >
                    Выбрать нанесения
                  </Button>
                </div>
                {selectedCoatings.length > 0 ? (
                  <ListGroup>
                    {selectedCoatings.map((coating) => (
                      <ListGroup.Item key={coating.id} className="py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="small">{coating.name}</span>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeCoating(coating.id)}
                          >
                            ×
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Alert variant="info" className="small mb-0 py-2">
                    Нанесения не выбраны
                  </Alert>
                )}
              </div>
              <Form.Text className="text-muted">
                Выберите нанесения, которые доступны для этого продукта
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Стандартный вес</Form.Label>
              <Form.Control
                required
                type="number"
                value={standardWeight}
                onChange={(e) => setStandardWeight(e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Подтип</Form.Label>
              <Form.Select
                value={subtypeId}
                onChange={(e) => setSubtypeId(e.target.value)}
              >
                <option value="">Без подтипа</option>
                {subtypes.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">Необязательное поле</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Материал</Form.Label>
              <Form.Select
                value={materialId}
                onChange={(e) => setMaterialId(e.target.value)}
              >
                <option value="">Без материала</option>
                {materials.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="use-exact-weights-create"
                label="Конкретные значения веса (выбор из списка размеров)"
                checked={useExactWeights}
                onChange={(e) => setUseExactWeights(e.target.checked)}
              />
            </Form.Group>

            {isSimpleProduct && (
              <Form.Group className="mb-3">
                <Form.Label>Цена</Form.Label>
                <Form.Control
                  required
                  type="number"
                  value={standardPrice}
                  onChange={(e) => setStandardPrice(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </Form.Group>
            )}
            {!isSimpleProduct &&
              <Stack direction="vertical">
                <Form.Group className="mb-3">
                  <Form.Label>Максимальный размер</Form.Label>
                  <Form.Control
                    required
                    type='number'
                    value={maxSize}
                    onChange={(e) => setMaxSize(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Минимальный размер</Form.Label>
                  <Form.Control
                    required
                    type='number'
                    value={minSize}
                    onChange={(e) => setMinSize(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Стандартная толщина</Form.Label>
                  <Form.Control
                    required
                    type='number'
                    value={standardThickness}
                    onChange={(e) => setStandardThickness(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Стандартный размер</Form.Label>
                  <Form.Control
                    required
                    type='number'
                    value={standardSize}
                    onChange={(e) => setStandardSize(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Group>
              </Stack>
            }
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onHide}>Отмена</Button>
            <Button variant="success" type="submit">Создать</Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <CoatingsSelectionModal />
    </>
  );
}